import React, {useState} from 'react';
import Section from '../Section';
import SectionContents from '../SectionContents';

import styles from './styles.module.scss';
import {Link} from 'react-router-dom';
import MuiIcon from 'components/Icons/MuiIcon';
import {first, keys} from 'lodash';

import {ENABLE_ANALYTICS, ENABLE_ISO_SURVEY} from 'configs/enable-features';
import HelpSectionDropdown from './HelpSectionDropdown';
import HelpSectionTabs from './HelpSectionTabs';
import HelpSectionBody from './HelpSectionBody';
import SectionContentsBody from '../SectionContentsBody';
import {CDN_IMAGE_URL} from 'configs';
import urlJoin from 'url-join';

export const SERVICES = {
  verification: {
    title: 'VERIFICATION',
    icon: 'check_circle',
    color: 'primary',
    items: [
      {
        value: 'check_iso',
        title: 'Check ISO certificate online',
        icon: 'search',
        description:
          'Search by company name or certificate details to look up ISO certification and confirm validity.',
        video: urlJoin(
          CDN_IMAGE_URL,
          '/p/landing/animations/manual-search.mp4'
        ),
        videoProps: {loading: 'eager'},
        cta: {
          type: Link,
          props: {
            to: '/search/certified-entities',
            children: (
              <>
                Search certificate <MuiIcon name="arrow_forward" />
              </>
            ),
          },
        },
      },
      {
        value: 'bulk_verify',
        title: 'Bulk Verify',
        icon: 'upload',
        iconProps: {variant: 'outlined'},
        description: (
          <>
            <p>
              Upload a file to instantly check ISO certified companies. Verify
              up to 10,000+ companies at once and access the full ISO certified
              company list, including locations and applicable standards.
            </p>
            <p className="mb-0">
              Upload a file to instantly check if up to 10,000 companies are
              certified, their locations, and all applicable standards.
            </p>
          </>
        ),
        video: urlJoin(
          CDN_IMAGE_URL,
          '/p/landing/animations/bulk-verification.mp4'
        ),
        cta: {
          type: Link,
          props: {
            to: '/services/bulk-certificate-verification',
            children: (
              <>
                Learn more <MuiIcon name="arrow_forward" />
              </>
            ),
          },
        },
      },
      {
        value: 'api',
        title: 'API',
        icon: 'api',
        description:
          'Verify unlimited certifications through the IAF CertSearch API and stream the data into your systems in real time.',
        video: urlJoin(
          CDN_IMAGE_URL,
          '/p/landing/animations/api-verification.mp4'
        ),
        cta: {
          type: Link,
          props: {
            to: '/services/api-certificate-search',
            children: (
              <>
                Request API Access <MuiIcon name="arrow_forward" />
              </>
            ),
          },
        },
      },
      {
        value: 'alerts',
        title: 'Real-time alerts',
        icon: 'notifications',
        iconProps: {variant: 'outlined'},
        description:
          'Get alerts when certificates are suspended, withdrawn, or expired, helping you manage risk in real time.',
        image: urlJoin(
          CDN_IMAGE_URL,
          'p/landing/how-we-help/certificate-monitoring.png'
        ),
        cta: {
          type: Link,
          props: {
            to: '/services/api-certificate-search',
          },
        },
      },
    ],
  },
  analytics: {
    title: 'ANALYTICS',
    icon: 'insights',
    color: 'discovery',
    items: [
      {
        value: 'standards',
        title: 'I want analytics on all standards',
        icon: 'insights',
        description:
          'Access dynamic anonymised month on month trend analysis for certificates, sites and entities for all countries, standard and sectors, leveraging benchmarks. With historical data dating back to 1993. ',
        video: urlJoin(
          CDN_IMAGE_URL,
          '/p/landing/animations/market-intelligence.mp4'
        ),
        cta: ENABLE_ANALYTICS && {
          type: Link,
          props: {
            to: '/services/certificate-market-intelligence',
            children: (
              <>
                Explore Market Intelligence <MuiIcon name="arrow_forward" />
              </>
            ),
          },
        },
      },
      {
        value: 'iso_survey',
        title: 'I want ISO survey report',
        icon: 'insights',
        description: 'I want to access the most recent ISO survey report',
        video: urlJoin(CDN_IMAGE_URL, '/p/landing/animations/iso-survey.mp4'),
        cta: ENABLE_ANALYTICS &&
          ENABLE_ISO_SURVEY && {
            type: Link,
            props: {
              to: '/services/iso-survey',
              children: (
                <>
                  Explore ISO Survey <MuiIcon name="arrow_forward" />
                </>
              ),
            },
          },
      },
    ],
  },
  certified: {
    title: 'GET CERTIFIED',
    icon: 'approval',
    color: 'success',
    items: [
      {
        value: 'certified',
        title: 'I want to become certified',
        icon: 'approval',
        description:
          'Become certified by an accredited certification body to appear in the official global database.',
        image: urlJoin(
          CDN_IMAGE_URL,
          'p/landing/how-we-help/i-want-to-become-certifiedv2.png'
        ),
        cta: {
          type: Link,
          props: {
            to: '/search/certification-bodies',
            children: (
              <>
                View Accredited Certification Bodies Here{' '}
                <MuiIcon name="arrow_forward" />
              </>
            ),
          },
        },
      },
      {
        value: 'standards_cover',
        title: 'I want to know which standards IAF CertSearch cover',
        icon: 'approval',
        description:
          'CertSearch covers > 300 standards management system standards. All ISO, national, and sector schemes, issued by certification bodies accredited by IAF member accreditation bodies.',
        image: urlJoin(CDN_IMAGE_URL, 'p/landing/standards-cover.png '),
      },
    ],
  },
};

function Index() {
  const [tab, setTab] = useState(first(keys(SERVICES)));

  const toggle = (value) => setTab(value);

  return (
    <Section>
      <SectionContents sizes={{xs: [12, 12]}}>
        <div className={styles['help-section']}>
          <SectionContentsBody title="How can we help you today?" />
          <HelpSectionDropdown onSelect={toggle} selected={tab} />
          <HelpSectionTabs tab={tab} toggle={toggle} />
          <HelpSectionBody items={SERVICES[tab].items} tab={tab} />
        </div>
      </SectionContents>
    </Section>
  );
}

export default Index;
